import { useLang } from 'hooks/lang';

import { calculateServiceCost, ProjectService, RepriceControl } from 'entities';

import {
  Card,
  Column,
  IconButton,
  Label,
  LabeledCheckbox,
  Row,
  Tag,
  Textarea,
} from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';
import { EyeIcon } from 'components/Icons';

import { formatCurrency } from 'utils/text';
import { getCountryLabel } from 'utils/countries';

import { useProjectProfile } from '../../../context';

interface CountrySelectProps {
  children?: never;

  selectedServices: string[];

  repriceControl: RepriceControl;

  serviceMap: Record<
    string,
    ProjectService & {
      taskName: string;
      taskId: string;
      taskStatus: string;
      label: string;
      disabled: boolean;
    }
  >;

  handleCountryClick(serviceId: string, country: string): void;
  onJustification(serviceId: string, country: string, value: string): void;
}

export function CountrySelect({
  serviceMap,
  selectedServices,
  handleCountryClick,
  onJustification,
  repriceControl,
}: CountrySelectProps) {
  const { t } = useLang();

  const { project, openTask } = useProjectProfile();

  if (!selectedServices.length) return null;

  return (
    <Column>
      <Label>{t('choose-which-countries')}</Label>

      {selectedServices.map((serviceId) => (
        <Card flow="column" key={serviceId}>
          <Row gap="2">
            <span className="max-w-[50%]">{serviceMap[serviceId].label}</span>

            <IconButton
              size="small"
              tooltipContent={t('open-task')}
              onClick={() => openTask(serviceMap[serviceId].taskId)}
            >
              <EyeIcon size={14} />
            </IconButton>

            <EntityStatusTag
              target="task"
              status={serviceMap[serviceId].taskStatus}
              size="tiny"
              round={false}
              className="w-fit"
            />

            {serviceMap[serviceId].outsourceConfig && (
              <Tag size="tiny" color="warning" name={t('outsourced')} />
            )}

            <span className="ml-auto text-xs">
              {formatCurrency(calculateServiceCost(serviceMap[serviceId]), project.currency)}
            </span>
          </Row>

          {project.countries.map((country) => (
            <Column key={country} gap="2">
              <Row gap="2">
                <LabeledCheckbox
                  label={getCountryLabel(country)}
                  checked={typeof repriceControl[serviceId][country] === 'string'}
                  onChange={() => handleCountryClick(serviceId, country)}
                  size="small"
                />

                <span className="ml-auto text-xs">
                  {formatCurrency(
                    calculateServiceCost({
                      ...serviceMap[serviceId],
                      costs: { [country]: serviceMap[serviceId].costs[country] },
                    }),
                    project.currency,
                  )}
                </span>
              </Row>

              {typeof repriceControl[serviceId][country] === 'string' && (
                <Textarea
                  placeholder={t('why')!}
                  value={repriceControl[serviceId][country] || ''}
                  onChange={({ target }) => onJustification(serviceId, country, target.value)}
                />
              )}
            </Column>
          ))}
        </Card>
      ))}
    </Column>
  );
}
